import { Link, useNavigate } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { TwoFactorChallenge } from '@nucleus-commerce/api-client/admin';
import { isApiError } from '@nucleus-commerce/api-client/transport';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';

import { toMagicLinkVerify } from '../api/mappers';
import { AuthPage } from '../components/auth-page';
import { ServerError } from '../components/server-error';
import { TwoFactorStep } from '../components/two-factor-step';
import { useVerifyMagicLink } from '../hooks/use-verify-magic-link';
import { isTwoFactorChallenge, type ChallengeEnd } from '../schemas/two-factor-rules';

export interface MagicLinkPageProps {
  token: string | undefined;
  /** `&organization=` the backend put into the link when the request named one. */
  organization?: string | undefined;
}

/**
 * Landing page of the emailed sign-in link. The token is single use, so it is exchanged exactly
 * once per page (the ref survives StrictMode's second effect run). A 202 continues with the
 * same code step as the password sign-in.
 */
export function MagicLinkPage({ token, organization }: MagicLinkPageProps) {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const verify = useVerifyMagicLink();
  const { mutate } = verify;
  const started = useRef<string | null>(null);
  const [challengeEnd, setChallengeEnd] = useState<ChallengeEnd | null>(null);
  const result = verify.data;
  const challenge: TwoFactorChallenge | null =
    result && isTwoFactorChallenge(result) && !challengeEnd ? result : null;
  const signedIn = result !== undefined && !isTwoFactorChallenge(result);

  const enter = useCallback(() => navigate({ to: '/org', replace: true }), [navigate]);
  const toSignIn = useCallback(() => navigate({ to: '/login' }), [navigate]);
  const onExpired = useCallback((reason: ChallengeEnd) => setChallengeEnd(reason), []);

  useEffect(() => {
    if (!token || started.current === token) return;
    started.current = token;
    mutate(toMagicLinkVerify(token, organization));
  }, [token, organization, mutate]);

  useEffect(() => {
    if (signedIn) void enter();
  }, [signedIn, enter]);

  const requestNew = (
    <Button asChild className="w-full">
      <Link to="/sign-in-link">{t('magicLink.requestNew')}</Link>
    </Button>
  );
  const footer = <Link to="/login">{t('links.backToSignIn')}</Link>;

  if (!token) {
    return (
      <AuthPage title={t('magicLink.invalidTitle')} footer={footer}>
        <div className="flex flex-col gap-4">
          <p className="text-muted-foreground text-sm">{t('magicLink.missingToken')}</p>
          {requestNew}
        </div>
      </AuthPage>
    );
  }

  if (challenge) {
    return (
      <AuthPage title={t('twoFactor.title')}>
        <TwoFactorStep
          challenge={challenge}
          onVerified={enter}
          onBack={() => void toSignIn()}
          onExpired={onExpired}
          backLabel={t('links.backToSignIn')}
        />
      </AuthPage>
    );
  }

  if (challengeEnd) {
    return (
      <AuthPage title={t(`twoFactor.${challengeEnd}Title`)} footer={footer}>
        <div className="flex flex-col gap-4">
          <Alert role="alert">
            <AlertDescription>
              {t(
                challengeEnd === 'expired'
                  ? 'magicLink.challengeExpired'
                  : 'magicLink.challengeInvalid',
              )}
            </AlertDescription>
          </Alert>
          {requestNew}
        </div>
      </AuthPage>
    );
  }

  if (verify.isError) {
    const invalid = isApiError(verify.error) && verify.error.kind === 'unauthorized';
    return (
      <AuthPage
        title={invalid ? t('magicLink.invalidTitle') : t('magicLink.failedTitle')}
        footer={footer}
      >
        <div className="flex flex-col gap-4">
          {invalid ? (
            <p className="text-muted-foreground text-sm">{t('magicLink.invalid')}</p>
          ) : (
            <ServerError error={verify.error} />
          )}
          {requestNew}
        </div>
      </AuthPage>
    );
  }

  return (
    <AuthPage title={t('magicLink.signingInTitle')}>
      <p role="status" className="text-muted-foreground flex items-center gap-2 text-sm">
        <Loader2 className="size-4 animate-spin" aria-hidden />
        {t('magicLink.signingIn')}
      </p>
    </AuthPage>
  );
}
